import React from "react";
import {
  Activity,
  Award,
  BarChart3,
  Calendar,
  ClipboardList,
  Clock,
  DollarSign,
  Kanban,
  Package,
  Settings,
  Shield,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { BenefitItem, CtaData, FeatureItem, RoleItem } from "./types";

const iconMap: Record<string, LucideIcon> = {
  Users,
  Kanban,
  Calendar,
  Clock,
  Package,
  ClipboardList,
  Settings,
  Award,
  BarChart3,
  DollarSign,
  Activity,
  Shield,
  UserCheck,
  Zap,
  Target,
  TrendingUp,
};

interface ContentSectionProps {
  readonly features: readonly FeatureItem[];
  readonly roles: readonly RoleItem[];
  readonly benefits: readonly BenefitItem[];
  readonly ctaData: Readonly<CtaData>;
  readonly onQuickInquiryClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function ContentSection({
  features,
  roles,
  benefits,
  ctaData,
  onQuickInquiryClick,
}: ContentSectionProps) {
  return (
    <>
      {/* Features */}
      <section id="features" className="py-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1b35] mb-3">
              Powerful Features for Modern Workforce
            </h2>
            <p className="text-base text-[#445676] max-w-2xl mx-auto">
              Everything you need to manage your workforce efficiently in one integrated platform
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 ">
            {features.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={feature.title}
                  className="p-5 bg-white rounded-xl shadow-sm border border-[#dde5f1] hover:shadow-md transition-shadow"
                >
                  <div className="w-11 h-11 bg-[#dce8ff] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#0f1b35] mb-2">{feature.title}</h3>
                  <p className="text-xs text-[#4a5d7f] leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="py-2 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1b35] mb-3">
              Role-Based Access Control
            </h2>
            <p className="text-base text-[#445676] max-w-2xl mx-auto">
              Tailored experiences for every role in your organization
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {roles.map((role) => {
              const Icon = iconMap[role.icon];
              return (
                <div key={role.title} className={`${role.iconWrapperClassName} p-5 rounded-xl`}>
                  <div className={`w-11 h-11 ${role.colorClassName} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-[#0f1b35] mb-3">{role.title}</h3>
                  <ul className="space-y-1.5 text-xs text-[#415373]">
                    {role.points.map((point) => (
                      <li key={point} className="flex items-start gap-2">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${role.bulletClassName} flex-shrink-0`} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#f8faff] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1b35] mb-3">
              Why Choose Liriworks?
            </h2>
            <p className="text-base text-[#334b73] max-w-2xl mx-auto">
              Transform your operations with these key benefits
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 bg-gradient-to-br  rounded-2xl px-6 py-1">
            {benefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div key={benefit.title} className="text-center">
                  <div className={`w-14 h-14 ${benefit.iconWrapperClassName} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-[#0f1b35] mb-1.5">{benefit.title}</h3>
                  <p className="text-xs text-[#334b73] leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-4 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1b35] mb-4">{ctaData.title}</h2>
          <p className="text-base text-[#445676] mb-8">{ctaData.subtitle}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={ctaData.primaryButton.href}
              className="px-6 py-3 bg-[#2563eb] text-white text-sm font-semibold rounded-lg hover:bg-[#1f52c9] transition-colors shadow-md"
            >
              {ctaData.primaryButton.label}
            </a>
            <button
              type="button"
              onClick={onQuickInquiryClick}
              className="px-6 py-3 !bg-[#2563eb] !text-white text-sm font-semibold rounded-lg hover:!bg-[#1f52c9] transition-colors shadow-md inline-flex items-center justify-center"
            >
              {ctaData.secondaryButton.label || "Quick Enquiry"}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}